import {HitDateUtils} from '@hit/components/src/utils/date/HitDateUtils';

export class PrintConfigService {
  static REUSABLE_CONFIGS = {
    storeFile: {
      id: 'store-file',
      type: 'boolean', // The type needs to match the used type in hit-input-generic component
      label: 'hit-app.printer-configuration.store-file',
      default: false,
    },
    displayAmountInWords: {
      id: 'displayAmountInWords',
      type: 'boolean',
      label: 'hit-app.custom-print-config.transaction-display-amount-words',
      default: false,
    },
    displayPositionTitleSummary: {
      id: 'displayPositionTitleSummary',
      type: 'boolean',
      label:
        'hit-app.custom-print-config.transaction-display-position-title-summary',
      default: false,
    },
    newPageForTitleLevel1: {
      id: 'newPageForTitleLevel1',
      type: 'boolean',
      label: 'hit-app.custom-print-config.project-part-new-page-title-level-1',
      default: false,
    },
    displayDiscount: {
      id: 'displayDiscount',
      type: 'boolean',
      label: 'hit-app.custom-print-config.transaction-display-discount',
      default: false,
    },
    printSiteAddress: {
      id: 'printSiteAddress',
      type: 'boolean',
      label: 'hit-app.custom-print-config.transaction-print-site-address',
      default: false,
    },
    fromDateTime: {
      id: 'fromDate',
      type: 'datetime-local', // The type needs to match the used type in hit-input-generic component
      label: 'hit-base.common.from',
      default: HitDateUtils.setToLocalMidnight(new Date()),
    },
    toDateTime: {
      id: 'toDate',
      type: 'datetime-local',
      label: 'hit-base.common.to',
      default: HitDateUtils.setToLocalEndOfDay(new Date()),
    },
    fromDate: {
      id: 'fromDate',
      type: 'date',
      label: 'hit-base.common.from',
      default: HitDateUtils.firstDayLastMonth(),
    },
    toDate: {
      id: 'toDate',
      type: 'date',
      label: 'hit-base.common.to',
      default: HitDateUtils.lastDayLastMonth(),
    },
    firstDayOfWeek: {
      id: 'firstDayOfWeek',
      type: 'date',
      label: 'hit-base.common.from',
      default: HitDateUtils.firstDayOfWeek(new Date()),
    },
    lastDayOfWeek: {
      id: 'lastDayOfWeek',
      type: 'date',
      label: 'hit-base.common.to',
      default: HitDateUtils.lastDayOfWeek(new Date()),
    },
    month: {
      id: 'month',
      type: 'month',
      label: 'hit-app.timeslot.calendar-month',
      default: (() => {
        const d = new Date();
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
      })(),
    },
    fromItemID: {
      id: 'fromItemID',
      type: 'entity',
      label: 'hit-app.project-part-item.from-project-part-item',
      entity: 'position-item',
      required: false,
      default: '',
      propertyPathToDisplay: ['fastModeBrowserDesignation'],
    },
    toItemID: {
      id: 'toItemID',
      type: 'entity',
      label: 'hit-app.project-part-item.to-project-part-item',
      entity: 'position-item',
      required: false,
      default: '',
      propertyPathToDisplay: ['fastModeBrowserDesignation'],
    },
    fromResID: {
      id: 'fromResID',
      type: 'entity',
      label: 'hit-app.resource.res',
      entity: 'resource',
      required: false,
      default: '',
      propertyPathToDisplay: ['fastModeBrowserDesignation'],
    },
    toResID: {
      id: 'toResID',
      type: 'entity',
      label: 'hit-app.resource.to-res',
      entity: 'resource',
      required: false,
      default: '',
      propertyPathToDisplay: ['fastModeBrowserDesignation'],
    },
  };

  static TEMPLATE_CONFIGS = {
    actionSummary: [
      PrintConfigService.REUSABLE_CONFIGS.fromDateTime,
      PrintConfigService.REUSABLE_CONFIGS.toDateTime,
    ],
    transactionDefault: [
      PrintConfigService.REUSABLE_CONFIGS.displayAmountInWords,
      PrintConfigService.REUSABLE_CONFIGS.displayPositionTitleSummary,
      PrintConfigService.REUSABLE_CONFIGS.newPageForTitleLevel1,
      PrintConfigService.REUSABLE_CONFIGS.displayDiscount,
      PrintConfigService.REUSABLE_CONFIGS.printSiteAddress,
    ],
    transactionQuotation: [
      PrintConfigService.REUSABLE_CONFIGS.displayAmountInWords,
      PrintConfigService.REUSABLE_CONFIGS.displayPositionTitleSummary,
      PrintConfigService.REUSABLE_CONFIGS.newPageForTitleLevel1,
      PrintConfigService.REUSABLE_CONFIGS.displayDiscount,
    ],
    transactionSubmission: [
      PrintConfigService.REUSABLE_CONFIGS.displayAmountInWords,
      PrintConfigService.REUSABLE_CONFIGS.displayPositionTitleSummary,
      PrintConfigService.REUSABLE_CONFIGS.newPageForTitleLevel1,
    ],
    projectPartQuotation: [
      PrintConfigService.REUSABLE_CONFIGS.displayAmountInWords,
      PrintConfigService.REUSABLE_CONFIGS.displayPositionTitleSummary,
      PrintConfigService.REUSABLE_CONFIGS.newPageForTitleLevel1,
      PrintConfigService.REUSABLE_CONFIGS.displayDiscount,
    ],
    projectPartSubmission: [
      PrintConfigService.REUSABLE_CONFIGS.displayAmountInWords,
      PrintConfigService.REUSABLE_CONFIGS.displayPositionTitleSummary,
      PrintConfigService.REUSABLE_CONFIGS.newPageForTitleLevel1,
    ],
    projectPartOrderConfirmation: [
      PrintConfigService.REUSABLE_CONFIGS.displayAmountInWords,
      PrintConfigService.REUSABLE_CONFIGS.newPageForTitleLevel1,
    ],
    projectPartBillOfQuantities: [
      PrintConfigService.REUSABLE_CONFIGS.newPageForTitleLevel1,
    ],
    projectPartCostSummary: [
      PrintConfigService.REUSABLE_CONFIGS.fromDate,
      PrintConfigService.REUSABLE_CONFIGS.toDate,
    ],
    projectPartCostDetails: [
      PrintConfigService.REUSABLE_CONFIGS.fromDate,
      PrintConfigService.REUSABLE_CONFIGS.toDate,
    ],
    staffIdentitySheet: [
      {
        id: 'printConfidentialData',
        type: 'boolean',
        label: 'hit-app.common.confidential-data',
        default: true,
        moduleKeys: ['staff-confidential-data'],
      },
      {
        id: 'printConfiguration',
        type: 'boolean',
        label: 'hit-app.common.configuration',
        default: true,
      },
      {
        id: 'printCommunication',
        type: 'boolean',
        label: 'hit-base.common.communication-mode',
        default: true,
      },
      {
        id: 'printMemo',
        type: 'boolean',
        label: 'hit-base.common.memo',
        default: true,
      },
      {
        id: 'printActions',
        type: 'boolean',
        label: 'hit-base.common.action',
        default: true,
        moduleKeys: ['action-base'],
      },
      {
        id: 'printForms',
        type: 'boolean',
        label: 'hit-base.common.form',
        default: true,
        moduleKeys: ['form-base'],
      },
    ],
    addressIdentitySheet: [
      {
        id: 'printIdentifiers',
        type: 'boolean',
        label: 'hit-app.common.identifier',
        default: true,
      },
      {
        id: 'printCommunication',
        type: 'boolean',
        label: 'hit-base.common.communication-mode',
        default: true,
      },
      {
        id: 'printTransactions',
        type: 'boolean',
        label: 'hit-app.transaction.general-title',
        default: true,
        moduleKeys: ['transaction-base'],
      },
      {
        id: 'printBankAccounts',
        type: 'boolean',
        label: 'hit-app.common.bank-account',
        default: true,
      },
      {
        id: 'printMemo',
        type: 'boolean',
        label: 'hit-base.common.memo',
        default: true,
      },
      {
        id: 'printRoles',
        type: 'boolean',
        label: 'hit-base.common.role',
        default: true,
      },
      {
        id: 'printContacts',
        type: 'boolean',
        label: 'hit-app.common.contact',
        default: true,
      },
      {
        id: 'printActions',
        type: 'boolean',
        label: 'hit-base.common.action',
        default: true,
        moduleKeys: ['action-base'],
      },
      {
        id: 'printForms',
        type: 'boolean',
        label: 'hit-base.common.form',
        default: true,
        moduleKeys: ['form-base'],
      },
      {
        id: 'printProjectParts',
        type: 'boolean',
        label: 'hit-base.common.project-part',
        default: true,
        moduleKeys: ['project-base'],
      },
    ],
    projectPartCo2EmissionOfPlannedResources: [
      PrintConfigService.REUSABLE_CONFIGS.fromDate,
      PrintConfigService.REUSABLE_CONFIGS.toDate,
    ],
    staffHoursDetails: [
      PrintConfigService.REUSABLE_CONFIGS.fromDate,
      PrintConfigService.REUSABLE_CONFIGS.toDate,
    ],
    staffHoursPerDayType: [
      {
        ...PrintConfigService.REUSABLE_CONFIGS.month,
        default: (() => {
          const d = new Date();
          d.setMonth(d.getMonth() - 1);
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          return `${year}-${month}`;
        })(),
      },
    ],
    staffHours: [
      PrintConfigService.REUSABLE_CONFIGS.fromDateTime,
      PrintConfigService.REUSABLE_CONFIGS.toDateTime,
    ],
    planningStaff: [
      PrintConfigService.REUSABLE_CONFIGS.firstDayOfWeek,
      PrintConfigService.REUSABLE_CONFIGS.lastDayOfWeek,
    ],
    planningEquipment: [
      PrintConfigService.REUSABLE_CONFIGS.firstDayOfWeek,
      PrintConfigService.REUSABLE_CONFIGS.lastDayOfWeek,
    ],
    planningProjectPart: [
      PrintConfigService.REUSABLE_CONFIGS.firstDayOfWeek,
      PrintConfigService.REUSABLE_CONFIGS.lastDayOfWeek,
    ],
    planningCalendarMonthly: [PrintConfigService.REUSABLE_CONFIGS.month],
    SalesSummary: [
      PrintConfigService.REUSABLE_CONFIGS.fromDate,
      PrintConfigService.REUSABLE_CONFIGS.toDate,
    ],
    PurchaseSummary: [
      PrintConfigService.REUSABLE_CONFIGS.fromDate,
      PrintConfigService.REUSABLE_CONFIGS.toDate,
    ],
    projectPartResourceAllocationToItems: [
      PrintConfigService.REUSABLE_CONFIGS.fromResID,
      PrintConfigService.REUSABLE_CONFIGS.fromItemID,
      PrintConfigService.REUSABLE_CONFIGS.toItemID,
    ],
    listOfCompositeResources: [
      PrintConfigService.REUSABLE_CONFIGS.fromResID,
      PrintConfigService.REUSABLE_CONFIGS.toResID,
    ],
    requiredResourcesForProjectPart: [
      PrintConfigService.REUSABLE_CONFIGS.fromItemID,
      PrintConfigService.REUSABLE_CONFIGS.toItemID,
    ],
    staffCostSummary: [
      PrintConfigService.REUSABLE_CONFIGS.fromDate,
      PrintConfigService.REUSABLE_CONFIGS.toDate,
    ],
    staffCostDetails: [
      PrintConfigService.REUSABLE_CONFIGS.fromDate,
      PrintConfigService.REUSABLE_CONFIGS.toDate,
    ],
    equipmentCostSummary: [
      PrintConfigService.REUSABLE_CONFIGS.fromDate,
      PrintConfigService.REUSABLE_CONFIGS.toDate,
    ],
    equipmentCostDetails: [
      PrintConfigService.REUSABLE_CONFIGS.fromDate,
      PrintConfigService.REUSABLE_CONFIGS.toDate,
    ],
  };

  static SUPPORTED_DYNAMIC_TYPES = new Set([
    'text',
    'number',
    'date',
    'datetime-local',
    'boolean',
    'month',
    'select',
    'entity',
  ]);

  static DATE_RANGE_CONFIG_TYPES = new Set(['date', 'datetime-local']);

  static DATE_RANGE_START_END_PREFIXES = [
    ['from', 'to'],
    ['start', 'end'],
    ['first', 'last'],
  ];

  static isDateRangeStartConfigId(configId) {
    const lower = String(configId).toLowerCase();
    return this.DATE_RANGE_START_END_PREFIXES.some(([startPrefix]) =>
      lower.startsWith(startPrefix),
    );
  }

  static findDateRangeEndId(startId) {
    const lower = String(startId).toLowerCase();
    for (const [startPrefix, endPrefix] of this.DATE_RANGE_START_END_PREFIXES) {
      if (lower.startsWith(startPrefix)) {
        return endPrefix + String(startId).slice(startPrefix.length);
      }
    }
    return null;
  }

  static hasInvalidDateRange(configs, values) {
    if (!Array.isArray(configs) || !values) {
      return false;
    }

    const dateConfigs = configs.filter((config) =>
      this.DATE_RANGE_CONFIG_TYPES.has(config.type),
    );
    const configIds = new Set(dateConfigs.map((config) => config.id));

    for (const startConfig of dateConfigs) {
      if (!this.isDateRangeStartConfigId(startConfig.id)) {
        continue;
      }

      const endId = this.findDateRangeEndId(startConfig.id);
      if (!endId || !configIds.has(endId)) {
        continue;
      }

      const startValue = values[startConfig.id];
      const endValue = values[endId];
      if (
        startValue === null ||
        startValue === undefined ||
        startValue === '' ||
        endValue === null ||
        endValue === undefined ||
        endValue === ''
      ) {
        continue;
      }

      const startDate = new Date(startValue);
      const endDate = new Date(endValue);
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        continue;
      }

      if (endDate < startDate) {
        return true;
      }
    }

    return false;
  }

  static buildDynamicConfig(schemaField) {
    const id = schemaField?.id || schemaField?.key;
    if (!id) return null;
    const type = schemaField?.type || 'text';
    return {
      id,
      type: this.SUPPORTED_DYNAMIC_TYPES.has(type) ? type : 'text',
      label: schemaField?.label || id,
      default: schemaField?.default ?? '',
      required: schemaField?.required === true,
      options: Array.isArray(schemaField?.options) ? schemaField.options : [],
      entity: schemaField?.entity || null,
      propertyPathToDisplay: schemaField?.propertyPathToDisplay || [
        'designation',
      ],
      source: schemaField?.source || null,
      sourceKey: schemaField?.sourceKey || null,
      sourceDefault: schemaField?.sourceDefault,
      visibleWhen: schemaField?.visibleWhen || null,
    };
  }

  /**
   * Returns a list of objects with one object per config that can be set for this template
   */
  static getTemplateConfigs(templateType, customReportDefinition = null) {
    const staticConfigs = this.TEMPLATE_CONFIGS?.[templateType] ?? [];
    const dynamicConfigs = Array.isArray(customReportDefinition?.paramsSchema)
      ? customReportDefinition.paramsSchema
          .map((field) => this.buildDynamicConfig(field))
          .filter(Boolean)
      : [];
    return [...staticConfigs, ...dynamicConfigs];
  }

  static parseLayoutValue(raw) {
    if (!raw) {
      return {};
    }
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return {};
      }
    }
    return raw;
  }

  static isCustomLabelLayout(layoutValue) {
    const value = PrintConfigService.parseLayoutValue(layoutValue);
    const page = value.page || {};
    const size = String(page.size || '').trim();
    if (size === 'custom') {
      return true;
    }
    if (/^\d+(\.\d+)?\s+\d+(\.\d+)?/.test(size)) {
      return true;
    }
    if (value.customPageWidthMm || value.customPageHeightMm) {
      return true;
    }
    if (value.customWidthMm || value.customHeightMm) {
      return true;
    }
    return false;
  }

  /**
   * Layouts allowed for stock resource labels: custom mm sizes + A4 portrait.
   */
  static isStockResourceLabelsLayoutAllowed(layoutRow) {
    const layoutValue = PrintConfigService.parseLayoutValue(layoutRow?.value);
    if (PrintConfigService.isCustomLabelLayout(layoutValue)) {
      return true;
    }
    const page = layoutValue.page || {};
    const size = String(page.size || '')
      .trim()
      .toUpperCase();
    const orientation = (page.orientation || 'portrait').toLowerCase();
    return size === 'A4' && orientation !== 'landscape';
  }
}
